/**
 * THE PRIVACY NOTICE ON A SHOP'S PAGES.
 *
 * A shop that advertises on Meta runs its pixel on its pages (utils/metaPixel), and Meta's
 * Business Tools Terms ask for a clear notice on every page where it runs, linking to an
 * explanation of the cookies and pixels in use and of how to opt out. That explanation is the
 * privacy policy page (Pages/PrivacyPolicy); this is the link to it, small and at the end of the
 * page, where a shopper looks for it.
 *
 * ONE ADDRESS, ON THE PLATFORM AND ON A BUSINESS'S OWN DOMAIN. `/privacy` is a route App.js
 * serves slug-less in both of its route trees and never under a shop's slug, so the link never
 * goes through `getBasePath`: on stylon.online `/<slug>/privacy` would fall through to the
 * catch-all, the login page, and on the business's own domain `/privacy` is already the page.
 *
 * A plain anchor. Where leaving the page would abandon something in progress — a half-filled
 * checkout, an open cart — it opens in a new tab (`newTab`); elsewhere it opens in place.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './privacyNotice.module.css';

export const PRIVACY_POLICY_PATH = '/privacy';

/** The link itself, for a footer that already lays out its own links. */
export function PrivacyPolicyLink({ t: tProp, className, newTab = false, testId = 'privacy-policy-link' }) {
  const { t: tOwn } = useTranslation();
  const t = tProp || tOwn;
  return (
    <a
      href={PRIVACY_POLICY_PATH}
      className={className}
      data-testid={testId}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {t('privacy', { defaultValue: 'Privacy Policy' })}
    </a>
  );
}

/** The notice as a line of its own, at the end of a shop page. */
export default function PrivacyNotice({ t, newTab = false, className = '' }) {
  return (
    <p className={`${styles.notice} ${className}`.trim()} data-testid="shop-privacy-notice">
      <PrivacyPolicyLink t={t} newTab={newTab} className={styles.link} />
    </p>
  );
}
